import { Sentence, HTMLBlock, copyRestObject, Book, BookLanguagePreset, Language } from "../../rest/models";
import { ModelMap } from "../../rest/slice";
import { AppDispatch } from "../../store/store";
import { escapeRegExp, groupBy, makePlainObject, orderBy, toDict } from "../../utils";
import { bookEditorActions } from "./edit/slice";

export const emptySentencePlaceholder = '***Empty Sentence Placeholder***';

export function parseBook(book: Book)
{
    book.is_html ? parseBlocks(book) : parseParagraphs(book);
}

export function recalculateBlocksAndSentences(book: Book, appDispatch: AppDispatch)
{
    book = makePlainObject(book);
    parseBook(book);
    appDispatch(bookEditorActions.setFormBook(book));
    appDispatch(bookEditorActions.sortAndSyncBookLanguages());

    return makePlainObject(book);
}

/**
 * Used if book.is_html is true.
 * 
 * While HTML distinguishes a whole bunch of display styles (inline, block, table-cell etc.), I have three types of blocks: 
 * container, inline and non-text.
 * 
 * As I traverse the children of an element, I combine them into blocks. If two or more adjacent elements are inline
 * (which includes text nodes, which are technically not elements at all), they are grouped into one Inline Block, which
 * is a child of the current Container Block.
 * 
 * If I encounter an element with a non-inline display style, I create a new Container Block as a child of the current one,
 * and recursively examine its children.
 * 
 * Aside from that, some special cases:
 *      <br> elements are treated as text nodes containing the string "\n".
 *      For elements that display images or other data that shouldn't be translated, there are the non-text elements.
 * 
 * Then, split the plain text of inline blocks into sentences.
 * Finally, assign each block and sentence a position.
 * The inline blocks are subsequently split into sentences, translated, and displayed to the user.
 */
function parseBlocks(book: Book)
{
    const nonTextElements = book.non_text_elements;
    const skipElements = book.skipped_elements;

    const tempElement = document.createElement('div');

    tempElement.innerHTML = book.source_text;
    tempElement.style.position = 'absolute';
    tempElement.style.top = '-1000000px';
    document.body.appendChild(tempElement);

    function makeBlock(type: 'c'|'i'|'n')
    {
        const b = copyRestObject(new HTMLBlock(), HTMLBlock);
        b.type = type;
        
        return {b, plain_text: '', alt: '', title: ''};
    }

    type Block = ReturnType<typeof makeBlock>;

    let blocks = [] as Block[];
    let nextBlockId = -1;

    let currentContainerBlock = makeBlock('c');
    currentContainerBlock.b.position = currentContainerBlock.b.block_id = ++nextBlockId;
    blocks.push(currentContainerBlock);

    let currentInlineBlock: Block | null = null;

    function process_recursively(node: Node)
    {
        let html = '';
        let plain_text = '';

        if (node.nodeType === Node.TEXT_NODE)
        {
            plain_text = html = node.textContent || '';
        }
        else if (node.nodeType === Node.ELEMENT_NODE)
        {
            const elm = node as Element;
            const tag = elm.tagName.toLowerCase();
            const style = window.getComputedStyle(elm)
            let display = style.display;
            let whitespace = style.whiteSpace;

            if ((whitespace.startsWith('pre') && !whitespace.includes('nowrap')) || whitespace.includes('break-spaces'))
            {
                elm.innerHTML = elm.innerHTML.replaceAll('\n', '<br/>');
            }

            if (display === 'none')
            {
                return;
            }
            else if (tag === 'br')
            {
                html = elm.outerHTML;
                plain_text = '\n';
                display = 'block';
            }

            if (elementMatchesSelector(elm, skipElements))
            {
                return;
            }
            else if (elementMatchesSelector(elm, nonTextElements))
            {
                currentInlineBlock = null;
                const b2 = makeBlock('c');
                b2.alt = elm.getAttribute('alt') ?? '';
                b2.title = elm.getAttribute('title') ?? '';

                const b = b2.b;

                b.type = 'n';
                b.position = b.block_id = ++nextBlockId;
                b.parent_block_id = currentContainerBlock.b.block_id;
                b.html = elm.outerHTML.trim();
                
                blocks.push(b2);
            }
            
            //Inline elements are combined with other inline elements around them.
            //Unless they contain images or other non-text elements (the common case being
            //an anchor containing an image), in which case they're treated as blocks.
            else if (display === 'inline')
            {
                html = elm.outerHTML;
                plain_text = elm.textContent ?? '';
            }

            //Create a new container block and process its contents recursively.
            else
            {
                currentInlineBlock = null;
                const parent = currentContainerBlock;

                currentContainerBlock = makeBlock('c');
                const b = currentContainerBlock.b;

                b.position = b.block_id = ++nextBlockId;
                b.parent_block_id = parent.b.block_id;

                blocks.push(currentContainerBlock);

                for (let i = 0; i < node.childNodes.length; i++)
                {
                    const cn = node.childNodes[i];
                    process_recursively(cn);
                }

                //Reset after process_recursively().
                currentContainerBlock = parent;
                currentInlineBlock = null;

                return;
            }
        }

        if (html)
        {
            if (currentInlineBlock)
            {
                currentInlineBlock.b.html += html;
                currentInlineBlock.plain_text += plain_text;
            }
            else
            {
                currentInlineBlock = makeBlock('i');
                const b = currentInlineBlock.b;
                b.position = b.block_id = ++nextBlockId;
                b.parent_block_id = currentContainerBlock.b.block_id;
                b.html = html.trim();

                currentInlineBlock.plain_text = plain_text;
                blocks.push(currentInlineBlock);
            }
        }
    }

    for (let i = 0; i < tempElement.childNodes.length; i++)
    {
        const cn = tempElement.childNodes[i];
        process_recursively(cn);
    }
    
    book.htmlblock_set = blocks.map(b => b.b);
    document.body.removeChild(tempElement);

    const abbrs = book
        .abbreviations
        .split(/[\n\r]+/)
        .map(a => a.trim())
        .filter(a => a);

    book.sentence_set = [];
    for (const b of blocks)
    {
        const texts = 
        [
            {sens: parseSentences(b.alt, abbrs), type: 'a'},
            {sens: parseSentences(b.title, abbrs), type: 't'},
            {sens: parseSentences(b.plain_text, abbrs), type: 'p'},
        ];

        const senCount =
            texts[0].sens.length
            + texts[1].sens.length
            + texts[2].sens.length;
        const increment = 1.0 / (senCount + 1);
        let i = 0;

        for (const t of texts)
        {
            for (const s0 of t.sens)
            {
                const s = copyRestObject(new Sentence(), Sentence);
                s.type = t.type as typeof s.type;
                s.text = s0 || emptySentencePlaceholder;
                s.parent_block_id = b.b.block_id;

                //The sentence's position is initially a float, so it's between this block and the next one.
                //When we run renumberBlocksAndSentences, it will become an integer.
                s.position = b.b.position + ((++i) * increment);
    
                book.sentence_set.push(s);
            }
        }

        if (b.b.type === 'n' && b.b.html)
        {
            const s = copyRestObject(new Sentence(), Sentence);
            s.type = 'n';
            s.html = b.b.html.trim() || null;
            s.text = emptySentencePlaceholder;
            s.parent_block_id = b.b.block_id;
            s.position = b.b.position + 1e-9;

            book.sentence_set.push(s);
        }
    }
    
    shrinkBlockHierarchy(book);
}

/**
 * Used if book.is_html is false.
 * 
 * Splits text into paragraphs and treats them as blocks.
 * The blocks don't store their contents in b.html.
 */
function parseParagraphs(book: Book)
{
    let nextPos = 0;
    let nextBlockId = 0;
    let nextSentenceId = 0;

    book.sentence_set = [];

    book.htmlblock_set = book
        .source_text
        .split(/[\n\r]+/)
        .map(p => p.trim())
        .filter(p => p)
        .map(function(p)
        {
            const b = copyRestObject(new HTMLBlock(), HTMLBlock);
            b.type = 'p';
            b.position = nextPos++;
            b.block_id = nextBlockId++;
            
            for (const s0 of parseSentences(p, book.abbreviations.split(/[\n\r]+/)))
            {
                const s = copyRestObject(new Sentence(), Sentence);
                s.type = 'p';
                s.text = s0;
                s.parent_block_id = b.block_id;
                s.sentence_id = nextSentenceId++;
                s.position = nextPos++;
    
                book.sentence_set.push(s);
            }

            return b;
        });
}

function parseSentences(text: string, abbrs: string[])
{
    text = text.trim();

    if (!text)
    {
        return [];
    }

    const ret = [] as string[][];

    const qm = `?﹖؟`; //Question marks: regular, small, Arabic. NOT the Spanish inverted one.
    const em = `!﹗՜`; //Exclamation marks: regular, small, Armenian. NOT the Spanish inverted one.
    const fs = `.。`; //Full stops.
    const cm = `,、`; //Commas.
    const sc = `;፤؛`; //Semicolons.
    const lq = `«《“「‘`; //Left quotes.
    const rq = `»》”」’`; //Right quotes.

    const full_stops = escapeRegExp(fs);
    const sentence_enders = escapeRegExp(fs + qm + em + '‽');
    //Note that this includes the Spanish inverted QM and EM, which go at the beginning of a sentence.
    const all_punctuation = escapeRegExp(sentence_enders + cm + sc + lq + rq + ":{}()[]'’`\"¿¡");

    //String ends with punctuation, including 1+ sentence ender.
    const reSentenceEnderWord = new RegExp(`[${all_punctuation}]*[${sentence_enders}]+[${all_punctuation}]*$`, 'u');

    //String is a single letter in any language, followed by 1+ full stops.
    const reOneLetterAbbrWord = new RegExp(`^\\p{L}[${full_stops}]+$`, 'u');

    let currentSentence = [] as string[];

    const words = text.split(/\s+/gu);
    for (const word of words)
    {
        currentSentence.push(word);

        //See if the word ends with punctuation. Punctuation in the middle of the word is irrelevant.
        //At least one of the symbols, not necessarily the last one, needs to be a sentence ender.
        const m2 = word.match(reSentenceEnderWord);
        if (!m2?.length)
        {
            continue;
        }

        //See if the word is a single letter followed by a period. These are almost always abbreviations,
        //especially of personal names - P. T. Barnum and such.
        const m3 = word.match(reOneLetterAbbrWord);
        if (m3?.length)
        {
            continue;
        }

        //See if the word is one of the abbrs.
        let isAbbr = false;
        for (let a of abbrs)
        {
            //If abbreviation ends with a period, remove it.
            a = a.replace(RegExp(`[${full_stops}]+$`, 'u'), '');
            
            //0+ punctuation, abbr, a full stop, more punctuation. Unicode, case-insensitive.
            //For example, for abbr "mr", recognizes 'mr.', 'mr.,' and '...Mr..!'.
            const reAbbr = new RegExp(`^[${all_punctuation}]*${a}[${full_stops}]+[${all_punctuation}]*$`, 'ui');
            if (word.match(reAbbr))
            {
                isAbbr = true;
                break;
            }
        }
        if (isAbbr)
        {
            continue;
        }

        //If the word has passed all these tests, it is the last word of a sentence.
        ret.push(currentSentence);
        currentSentence = [];
    }

    //If the loop ended with a non-qualifying word, there will be words left.
    //Make them into a sentence, too.
    if (currentSentence.length > 0)
    {
        ret.push(currentSentence);
    }

    return ret
        .map(words => words.join(' '))
        .filter(s => s);
}

/**
 * Remove blocks with no contents at all.
 * For container blocks that have only one child block and no sentences:
 * 1) Update the monochild block's children so their parent is the monochild's parent/
 * 2) Remove the monochild.
 * 3) Continue the same operation with the children.
 * 
 * At the end, renumber all the block_ids.
 */
export function shrinkBlockHierarchy(book: Book, recursive = false)
{
    const blocksToRemove = {} as {[block_id: number] : true};
    const blocksByParent = groupBy(book.htmlblock_set, b => b.parent_block_id, b => b.position);
    const sentencesByBlock = groupBy(book.sentence_set, s => s.parent_block_id, s => s.position);
    const blockDict = toDict(book.htmlblock_set, b => b.block_id, b => b);

    for (const b of book.htmlblock_set)
    {
        const childBlocks = blocksByParent[b.block_id] || [];
        const sentences = sentencesByBlock[b.block_id] || [];
        if (childBlocks.length < 2 && sentences.length === 0)
        {
            blocksToRemove[b.block_id] = true;
        }

        if (childBlocks.length === 1)
        {
            /*
                Search among the block's ancestors for one that doesn't have just one descendent.
                Set the child's parent to that ancestor.
                If they all have just one child, the child of this block becomes a root block, with null parent_block_id.
                As we work, we will change blocks that may be ancestors for blocks further down the line.
                That's not a problem - the outcome will be the same, since we need the new ancestor anyway.
            */
            let currentAncestor = blockDict?.[b.parent_block_id];
            let childBlocks2 = blocksByParent?.[currentAncestor?.block_id] ?? [];
            while (childBlocks2?.length === 1)
            {
                currentAncestor = blockDict?.[currentAncestor.parent_block_id];
                childBlocks2 = blocksByParent?.[currentAncestor?.block_id] ?? [];
            }

            childBlocks[0].parent_block_id = (currentAncestor?.block_id) ?? -1;
        }
    }

    const count1 = book.htmlblock_set.length;
    //Now, as promised, it's time to remove the blocks that were marked for removal.
    book.htmlblock_set = book.htmlblock_set.filter(b => !blocksToRemove[b.block_id]);

    const deleted = count1 - book.htmlblock_set.length;
    if (deleted > 0)
    {
        //If blocks were removed, it's possible that a block that used to have two children now has one.
        shrinkBlockHierarchy(book, true);
    }

    if (!recursive)
    {
        renumberBlocksAndSentences(book);
    }
}

export function renumberBlocksAndSentences(book: Book)
{
    book.htmlblock_set = orderBy(book.htmlblock_set, b => b.position);
    const blocksByParent = groupBy(book.htmlblock_set, b => b.parent_block_id);
    book.sentence_set = orderBy(book.sentence_set, s => s.position);
    const sentencesByBlock = groupBy(book.sentence_set, s => s.parent_block_id);

    let nextPos = -1;
    let nextBlockId = -1;
    let nextSentenceId = -1;

    //Before assigning new IDs, to avoid breaking dependencies, we create an object where we
    //store the new IDs. Block IDs for blocks, sentence IDs for sentences. NOT database IDs.
    const keyToNewPosAndId = {} as {[key: string]: {pos: number, id: number}};
    const oldToNewBlockId = {} as {[old: number]: number};
    function renumberBlock(b: HTMLBlock)
    {
        const newValues = {pos: ++nextPos, id: ++nextBlockId};
        keyToNewPosAndId[b.key] = newValues;
        oldToNewBlockId[b.block_id] = newValues.id;

        const childBlocks = blocksByParent[b.block_id] || [];
        const sentences = sentencesByBlock[b.block_id] || [];
        for (const cb of childBlocks)
        {
            renumberBlock(cb);
        }
        for (const s of sentences)
        {
            keyToNewPosAndId[s.key] = {pos: ++nextPos, id: ++nextSentenceId};
        }
    }

    const rootBlocks = blocksByParent[-1] || []; 
    for (const b of rootBlocks)
    {
        renumberBlock(b);
    }
    
    for (const b of book.htmlblock_set)
    {
        b.block_id = keyToNewPosAndId[b.key].id;
        b.position = keyToNewPosAndId[b.key].pos;
        b.parent_block_id = oldToNewBlockId[b.parent_block_id] ?? -1;
    }
    
    for (const s of book.sentence_set)
    {
        s.sentence_id = keyToNewPosAndId[s.key].id;
        s.position = keyToNewPosAndId[s.key].pos;
        s.parent_block_id = oldToNewBlockId[s.parent_block_id] ?? -1;
    }
}

/**
 * Computes the blocks that need to be displayed in order to show the book partially.
 * Starts by computing the sentences, then the hierarchy of blocks that contain them.
 * 
 * @param book The book.
 * @param startPosition The position of the sentence at which to start.
 * @param sentenceCount Number of sentences to include.
 */
export function getBlocksForPartialDisplay(book: Book, startPosition: number, endPosition: number)
{
    //Select the needed sentences.
    let sentences = orderBy(book.sentence_set, s => s.position).slice(startPosition, endPosition);
    
    //Now we need to collect the ancestors of these items. To avoid duplicates, we'll use dicts.
    const blocksByBlockId = toDict(book.htmlblock_set, b => b.block_id, b => b);
    const ret = {} as {[block_id: number] : true}

    /** Add the ancestors of the block to the dict, stopping if the current item (and its ancestors)
     * has already been addded. */
    function addAncestorChain(b: HTMLBlock | null)
    {
        while (b && !Object.hasOwn(ret, b.block_id))
        {
            ret[b.block_id] = true;
            b = blocksByBlockId[b.parent_block_id];
        }
    }

    for (const i of sentences)
    {
        const parent = blocksByBlockId[i.parent_block_id];
        addAncestorChain(parent);
    }

    return ret;
}

/**
 * Turns the preset into four strings:
 *   the name of the source language,
 *   the target languages, separated by commas,
 *   all languages in display order, separated by commas,
 *   a string that can be used to compare and sort the presets.
 * 
 * Returns null if all languages in the preset have been deleted.
 */
export function presetToText(preset: BookLanguagePreset, languageModels: ModelMap<Language>)
{
    const pLangs = orderBy(preset.presetlanguage_set, pl => pl.display_order);
    const source = languageModels[preset.source_language]?.name;

    const langs = pLangs
        .map(pl => languageModels[pl.language]?.name)
        .filter(ln => ln);

    const targets = langs.filter(pl => pl !== source).join(', ') || 'None';
    const order = langs.join(', ');
    const comparable = `${source}; ${order}`

    if (source || order[0])
    {
        return {source, targets, order, comparable}
    }

    return null;
}

function elementMatchesSelector(element: Element, selector: string) 
{
    try 
    {
        return element.matches(selector);
    } 
    catch (error) 
    {
        return false;
    }
}